// Integration types whose alerts live on the Clips page rather than the
// Alerts page. Mirrors `is_clips=True` in actuate_admin's integration enum
// (ailink, sentinel-verifier, clips).
export const CLIPS_INTEGRATION_TYPES: string[] = [
  "ailink",
  "sentinel-verifier",
  "clips",
];

export const isClipsIntegration = (
  integrationTypeName: string | null | undefined
): boolean => CLIPS_INTEGRATION_TYPES.includes(integrationTypeName ?? "");

type ClipsTab = "ailink" | "sentinel";

interface BreadcrumbLike {
  id: string | number;
  name?: string | number;
}

interface SeeAlertsSiteInfo {
  id: string | number;
  integration_type_name?: string | null;
  breadcrumb_info?: BreadcrumbLike[];
}

interface ImmixCredentialLike {
  username?: string;
  group?: { id?: string | number; name?: string } | null;
}

const clipsTabFor = (integrationTypeName: string): ClipsTab =>
  integrationTypeName === "sentinel-verifier" ? "sentinel" : "ailink";

const readCredentials = (storageKey: string): ImmixCredentialLike[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// The Clips page filters by immix credential (`user=`), whose group is one of
// the site's ancestor groups. Walk the breadcrumb chain until a credential in
// the cached list matches; the cached lists are populated at login by
// dataFetchers (`aiLinkUsers` / `sentinelUsers`).
const findCredentialUsername = (
  tab: ClipsTab,
  breadcrumbs: BreadcrumbLike[]
): string | null => {
  const credentials = readCredentials(
    tab === "sentinel" ? "sentinelUsers" : "aiLinkUsers"
  );
  if (!credentials.length) return null;
  for (const crumb of breadcrumbs) {
    const match = credentials.find(
      (credential) => String(credential.group?.id) === String(crumb.id)
    );
    if (match?.username) return match.username;
  }
  return null;
};

// Where the site page's "See Alerts" button should go: the Clips page for
// clips-based integrations, the Alerts page for everything else.
export const getSeeAlertsPath = (siteInfo: SeeAlertsSiteInfo): string => {
  const breadcrumbs = siteInfo.breadcrumb_info ?? [];
  const topGroupId = breadcrumbs[0]?.id;

  if (!isClipsIntegration(siteInfo.integration_type_name)) {
    return `/alerts?group=${topGroupId}&site=${siteInfo.id}`;
  }

  const tab = clipsTabFor(siteInfo.integration_type_name as string);
  const params = new URLSearchParams();
  params.set("tab", tab);
  const username = findCredentialUsername(tab, breadcrumbs);
  if (username) params.set("user", username);
  params.set("site", String(siteInfo.id));
  return `/clips?${params.toString()}`;
};
